import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircle2Icon, ClockIcon, MinusCircleIcon, CalendarIcon, DollarSignIcon, PlusIcon, ArrowLeftIcon } from 'lucide-react';
import { StateStatus, RenewalEntry, ChargeItem, LicenseData, User } from './types';
interface StateModalProps {
  user: User;
  stateName: string;
  isOpen: boolean;
  onClose: () => void;
  license: LicenseData;
  onUpdateLicense: (license: LicenseData) => void;
}
const STATUS_CONFIG: Record<StateStatus, {
  label: string;
  icon: typeof CheckCircle2Icon;
  activeClass: string;
}> = {
  not_started: {
    label: 'Not Started',
    icon: MinusCircleIcon,
    activeClass: 'bg-slate-700 text-slate-200'
  },
  in_progress: {
    label: 'In Progress',
    icon: ClockIcon,
    activeClass: 'bg-amber-500/20 text-amber-400'
  },
  complete: {
    label: 'Complete',
    icon: CheckCircle2Icon,
    activeClass: 'bg-emerald-500/20 text-emerald-400'
  }
};
const STATUS_ORDER: StateStatus[] = ['not_started', 'in_progress', 'complete'];
export function StateModal({
  user,
  stateName,
  isOpen,
  onClose,
  license,
  onUpdateLicense
}: StateModalProps) {
  const [isAddingRenewal, setIsAddingRenewal] = useState(false);
  const [newRenewalYear, setNewRenewalYear] = useState<number>(new Date().getFullYear() + 1);
  const [newRenewalFee, setNewRenewalFee] = useState<number>(0);
  const [newRenewalDate, setNewRenewalDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [newChargeName, setNewChargeName] = useState('');
  const [newChargeAmount, setNewChargeAmount] = useState<number>(0);
  const [isAddingCharge, setIsAddingCharge] = useState(false);
  const charges = license.charges || [];
  const chargesTotalCost = charges.reduce((sum, c) => sum + c.amount, 0);
  const handleStatusChange = (status: StateStatus) => {
    onUpdateLicense({
      ...license,
      status
    });
  };
  const handleAddCharge = () => {
    if (!newChargeName.trim()) return;
    const updatedCharges = [...charges, {
      name: newChargeName.trim(),
      amount: newChargeAmount
    }];
    const newCost = updatedCharges.reduce((sum, c) => sum + c.amount, 0);
    onUpdateLicense({
      ...license,
      charges: updatedCharges,
      cost: newCost
    });
    setNewChargeName('');
    setNewChargeAmount(0);
    setIsAddingCharge(false);
  };
  const handleRemoveCharge = (index: number) => {
    const updatedCharges = charges.filter((_, i) => i !== index);
    const newCost = updatedCharges.reduce((sum, c) => sum + c.amount, 0);
    onUpdateLicense({
      ...license,
      charges: updatedCharges,
      cost: newCost
    });
  };
  const handleStartDateChange = (date: string) => {
    onUpdateLicense({
      ...license,
      startDate: date
    });
  };
  const handleExpirationDateChange = (date: string) => {
    onUpdateLicense({
      ...license,
      expirationDate: date
    });
  };
  const handleSaveRenewal = () => {
    const newRenewals = [...license.renewals, {
      year: newRenewalYear,
      fee: newRenewalFee,
      date: newRenewalDate
    }].sort((a, b) => b.year - a.year);
    onUpdateLicense({
      ...license,
      renewals: newRenewals
    });
    setIsAddingRenewal(false);
    setNewRenewalYear(new Date().getFullYear() + 1);
    setNewRenewalFee(0);
    setNewRenewalDate(new Date().toISOString().split('T')[0]);
  };
  const handleRemoveRenewal = (index: number) => {
    const newRenewals = [...license.renewals];
    newRenewals.splice(index, 1);
    onUpdateLicense({
      ...license,
      renewals: newRenewals
    });
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-252">
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6" data-id="element-253">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-254" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="relative w-full max-w-lg bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-255">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900/50" data-id="element-256">
            <div className="flex items-center gap-3" data-id="element-257">
              <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-258">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-259" />
              </button>
              <div data-id="element-260">
                <h2 className="text-lg font-bold text-white" data-id="element-261">{stateName}</h2>
                <p className="text-xs text-slate-500" data-id="element-262">
                  {user.name} · #{user.nmlsId}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 -mr-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors" data-id="element-263">
              <XIcon className="w-5 h-5" data-id="element-264" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-6" data-id="element-265">
            {/* Status */}
            <div data-id="element-266">
              <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-2" data-id="element-267">
                Status
              </label>
              <div className="flex bg-slate-800/80 rounded-xl p-1 gap-1" data-id="element-268">
                {STATUS_ORDER.map(s => {
                const isActive = license.status === s;
                const config = STATUS_CONFIG[s];
                const Icon = config.icon;
                return <button key={s} onClick={() => handleStatusChange(s)} className={`relative flex-1 flex items-center justify-center gap-2 py-2.5 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive ? config.activeClass : 'text-slate-500 hover:text-slate-300 hover:bg-slate-700/30'}`} data-id="element-269">
                      <Icon className="w-4 h-4 flex-shrink-0" data-id="element-270" />
                      <span data-id="element-271">{config.label}</span>
                    </button>;
              })}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-272">
              <div data-id="element-273">
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-2" data-id="element-274">
                  Sponsorship Start Date
                </label>
                <div className="flex items-center bg-slate-800/50 border border-slate-700 rounded-xl px-3 py-2.5 focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/50 transition-all" data-id="element-275">
                  <CalendarIcon className="w-4 h-4 text-slate-500 mr-2" data-id="element-276" />
                  <input type="date" value={license.startDate || ''} onChange={e => handleStartDateChange(e.target.value)} className="w-full bg-transparent text-sm text-slate-200 focus:outline-none" style={{
                  colorScheme: 'dark'
                }} data-id="element-277" />
                </div>
              </div>

              <AnimatePresence data-id="element-278">
                {license.status === 'complete' && <motion.div initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} data-id="element-279">
                    <label className="block text-xs font-medium text-emerald-400/80 uppercase tracking-wider mb-2" data-id="element-280">
                      Expiration Date
                    </label>
                    <div className="flex items-center bg-emerald-500/[0.05] border border-emerald-500/20 rounded-xl px-3 py-2.5 focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/50 transition-all" data-id="element-281">
                      <CalendarIcon className="w-4 h-4 text-emerald-500/70 mr-2" data-id="element-282" />
                      <input type="date" value={license.expirationDate || ''} onChange={e => handleExpirationDateChange(e.target.value)} className="w-full bg-transparent text-sm text-slate-200 focus:outline-none" style={{
                    colorScheme: 'dark'
                  }} data-id="element-283" />
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Charges */}
            <div data-id="element-284">
              <div className="flex items-center justify-between mb-3" data-id="element-285">
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-286">
                  License Charges
                </label>
                {!isAddingCharge && <button onClick={() => setIsAddingCharge(true)} className="flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 transition-colors bg-emerald-500/10 px-2.5 py-1.5 rounded-lg" data-id="element-287">
                    <PlusIcon className="w-3.5 h-3.5" data-id="element-288" />
                    Add Charge
                  </button>}
              </div>

              {charges.length > 0 && <div className="space-y-1.5 mb-3" data-id="element-289">
                  {charges.map((charge, idx) => <div key={idx} className="flex items-center justify-between bg-slate-800/40 rounded-lg px-3 py-2 border border-slate-700/50 group" data-id="element-290">
                      <span className="text-sm text-slate-300" data-id="element-291">
                        {charge.name}
                      </span>
                      <div className="flex items-center gap-3" data-id="element-292">
                        <span className="text-sm text-slate-200 tabular-nums font-medium" data-id="element-293">
                          ${charge.amount.toLocaleString()}
                        </span>
                        <button onClick={() => handleRemoveCharge(idx)} className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all p-0.5" aria-label="Remove charge" data-id="element-294">
                          <XIcon className="w-3.5 h-3.5" data-id="element-295" />
                        </button>
                      </div>
                    </div>)}
                </div>}

              <AnimatePresence data-id="element-296">
                {isAddingCharge && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden mb-3" data-id="element-297">
                    <div className="bg-slate-800/60 rounded-xl border border-slate-700 p-4" data-id="element-298">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3" data-id="element-299">
                        <div data-id="element-300">
                          <label className="block text-xs text-slate-400 mb-1.5" data-id="element-301">
                            Charge Name
                          </label>
                          <input type="text" value={newChargeName} onChange={e => setNewChargeName(e.target.value)} placeholder="e.g. Application Fee" className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/50 placeholder:text-slate-600" data-id="element-302" />
                        </div>
                        <div data-id="element-303">
                          <label className="block text-xs text-slate-400 mb-1.5" data-id="element-304">
                            Amount
                          </label>
                          <div className="flex items-center bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 focus-within:border-emerald-500/50" data-id="element-305">
                            <DollarSignIcon className="w-4 h-4 text-slate-500 mr-1.5" data-id="element-306" />
                            <input type="number" min="0" value={newChargeAmount === 0 ? '' : newChargeAmount} placeholder="0" onChange={e => setNewChargeAmount(parseInt(e.target.value) || 0)} className="w-full bg-transparent text-sm text-slate-200 tabular-nums focus:outline-none" data-id="element-307" />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-3" data-id="element-308">
                        <button onClick={() => {
                      setIsAddingCharge(false);
                      setNewChargeName('');
                      setNewChargeAmount(0);
                    }} className="text-sm text-slate-400 hover:text-slate-300 font-medium px-3 py-1.5" data-id="element-309">
                          Cancel
                        </button>
                        <button onClick={handleAddCharge} disabled={!newChargeName.trim()} className="text-sm bg-emerald-500 text-white px-4 py-1.5 rounded-lg hover:bg-emerald-600 transition-colors font-medium shadow-sm disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-310">
                          Add
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Total */}
              <div className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2.5 border border-slate-700/50" data-id="element-311">
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-312">
                  Total License Cost
                </span>
                <span className="text-sm font-bold text-white tabular-nums" data-id="element-313">
                  ${chargesTotalCost.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Renewals */}
            <AnimatePresence data-id="element-314">
              {license.status === 'complete' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="pt-4 border-t border-slate-800" data-id="element-315">
                  <div className="flex items-center justify-between mb-4" data-id="element-316">
                    <label className="block text-xs font-medium text-blue-400/80 uppercase tracking-wider" data-id="element-317">
                      Renewals
                    </label>
                    {!isAddingRenewal && <button onClick={() => setIsAddingRenewal(true)} className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors bg-blue-500/10 px-2.5 py-1.5 rounded-lg" data-id="element-318">
                        <PlusIcon className="w-3.5 h-3.5" data-id="element-319" />
                        Add Renewal
                      </button>}
                  </div>

                  {license.renewals.length > 0 && <div className="space-y-2 mb-4" data-id="element-320">
                      {license.renewals.map((renewal, idx) => <div key={idx} className="flex items-center justify-between bg-slate-800/40 rounded-xl px-4 py-3 border border-slate-700/50 group" data-id="element-321">
                          <div className="flex flex-col" data-id="element-322">
                            <span className="text-sm text-slate-200 font-medium" data-id="element-323">
                              {renewal.year} Renewal
                            </span>
                            <span className="text-xs text-slate-500" data-id="element-324">
                              Submitted: {renewal.date}
                            </span>
                          </div>
                          <div className="flex items-center gap-4" data-id="element-325">
                            <span className="text-sm text-slate-300 tabular-nums font-medium" data-id="element-326">
                              ${renewal.fee}
                            </span>
                            <button onClick={() => handleRemoveRenewal(idx)} className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all p-1" aria-label="Remove renewal" data-id="element-327">
                              <XIcon className="w-4 h-4" data-id="element-328" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <AnimatePresence data-id="element-329">
                    {isAddingRenewal && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="overflow-hidden" data-id="element-330">
                        <div className="bg-slate-800/60 rounded-xl border border-slate-700 p-4" data-id="element-331">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4" data-id="element-332">
                            <div data-id="element-333">
                              <label className="block text-xs text-slate-400 mb-1.5" data-id="element-334">
                                Year
                              </label>
                              <input type="number" value={newRenewalYear} onChange={e => setNewRenewalYear(parseInt(e.target.value) || new Date().getFullYear())} className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500/50" data-id="element-335" />
                            </div>
                            <div data-id="element-336">
                              <label className="block text-xs text-slate-400 mb-1.5" data-id="element-337">
                                Fee
                              </label>
                              <div className="flex items-center bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 focus-within:border-blue-500/50" data-id="element-338">
                                <DollarSignIcon className="w-4 h-4 text-slate-500 mr-1.5" data-id="element-339" />
                                <input type="number" min="0" value={newRenewalFee === 0 ? '' : newRenewalFee} placeholder="0" onChange={e => setNewRenewalFee(parseInt(e.target.value) || 0)} className="w-full bg-transparent text-sm text-slate-200 tabular-nums focus:outline-none" data-id="element-340" />
                              </div>
                            </div>
                            <div data-id="element-341">
                              <label className="block text-xs text-slate-400 mb-1.5" data-id="element-342">
                                Date Submitted
                              </label>
                              <input type="date" value={newRenewalDate} onChange={e => setNewRenewalDate(e.target.value)} className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500/50" style={{
                          colorScheme: 'dark'
                        }} data-id="element-343" />
                            </div>
                          </div>
                          <div className="flex items-center justify-end gap-3" data-id="element-344">
                            <button onClick={() => setIsAddingRenewal(false)} className="text-sm text-slate-400 hover:text-slate-300 font-medium px-3 py-1.5" data-id="element-345">
                              Cancel
                            </button>
                            <button onClick={handleSaveRenewal} className="text-sm bg-blue-500 text-white px-4 py-1.5 rounded-lg hover:bg-blue-600 transition-colors font-medium shadow-sm" data-id="element-346">
                              Save Renewal
                            </button>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/50 flex justify-end" data-id="element-347">
            <button onClick={onClose} className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-xl transition-colors" data-id="element-348">
              Done
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}